<template>
  <v-dialog
      v-model="show"
      persistent
      width="500"
      height="200"
    >
      <v-card>
        <v-card-title>
          <span class="headline">New transaction</span>
        </v-card-title>
        <v-card-text>
          <v-row no-gutters>
            <v-col md="5">
              <date-field :date.sync="txObj.date"/>
            </v-col>
          </v-row>
          <v-row no-gutters>
            <v-col cols="8">
              <v-autocomplete
                v-model="txObj.counterparty"
                :items="counterparties"
                item-text="name"
                label="Counterparty"
                prepend-icon="mdi-account"
                return-object
              >
                <template v-slot:no-data>
                  <p class="px-4 pt-2 text-center">                          
                    <v-btn
                      class="px-auto"
                      color="primary"
                      @click="onCreateNewCounterparty()"
                      >Create new</v-btn
                    >
                  </p>
                </template>
              </v-autocomplete>
            </v-col>
          </v-row>
          
          <v-row no-gutters>
            <v-col cols="8">
              <v-text-field
                autocomplete="off"
                label="Description"
                v-model="txObj.description"
                prepend-icon="mdi-file-document-outline"
              ></v-text-field>
            </v-col>
          </v-row>

          <v-row no-gutters v-if="isSimpleMode()">
            <v-col cols="8">
              <v-select
                v-model="txObj.transactionType"
                :items="transactionTypes"
                item-text="name"
                label="Transaction type"
                prepend-icon="mdi-tag"
                @input="updateEntries()"
                return-object
              ></v-select>
            </v-col>
            <v-col cols="7" md="5">
              <amount-field 
                :amount.sync="txObj.transactionAmount"
                @onAmount="updateEntries()" 
                :currencyDisabled="false"
                icon="mdi-cash"/>
            </v-col>
          </v-row>
          <div v-if="isAdvancedMode()">
            <v-row  v-for="(entry, i) in txObj.entries" :key="i">
              <v-col class="py-0" cols="6" md="7">
                <v-select
                  v-model="entry.account"
                  :items="accounts"
                  class="text-md-body-1 text-caption"
                  item-text="fname"
                  label="Account"
                  prepend-icon="mdi-book-open-blank-variant"
                  return-object
                  required
                ></v-select>
              </v-col>
              <v-col class="py-0 px-0" cols="5" md="4">
                <amount-field 
                  :amount.sync="entry.amount"
                  :currencyDisabled="false"/>
              </v-col>
              <v-col cols="1" class="d-flex ml-n6">
                <v-btn text x-small class="align-self-center" @click="onRemoveEntry(entry)">
                  <v-icon>mdi-table-row-remove</v-icon>
                </v-btn>
              </v-col>
            </v-row>
          </div>
          <v-row v-if="isAdvancedMode()">
            <v-col cols="12">
              <v-btn text x-small color="secondary" @click="onAddEntry()">
                <v-icon>mdi-table-row-plus-after</v-icon>
              </v-btn>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <v-btn x-small color="secondary" v-if="isSimpleMode()" @click="advancedMode()">Advanced</v-btn>
              <v-btn x-small color="secondary" v-if="isAdvancedMode()" @click="simpleMode()">Simple</v-btn>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="8">
               <v-file-input
                  multiple
                  placeholder="Select files to attach"
                  label="Attachment"
                  v-model="filesToUpload"
                >
              </v-file-input>
            </v-col>
          </v-row>
          <v-row>
          <v-col cols="3" class="d-flex justify-start"
            v-for="(attachment, i) in txObj.attachments" :key="i">
              <v-card min-height="100px" min-width="100px" color="#385F73">
                <v-card-title class="d-flex justify-end pr-1">
                  <v-btn fab absolute x-small text @click="onRemoveAttachment(attachment)">
                    <v-icon x-small >mdi-close</v-icon>
                  </v-btn>
                </v-card-title>
                <v-card-text class="white--text" @click="onPreview(attachment)">
                  {{attachment.name | truncate15}}
                </v-card-text>
              </v-card>
          </v-col>
          </v-row>

        </v-card-text>
        <v-divider></v-divider>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" class="ml-2" text @click="onClose()">Close</v-btn>
          <v-btn color="primary" @click="onSave()">Save</v-btn>
        </v-card-actions>        
      </v-card>
      <confirmation-dialog 
        dialogType="info"
        :alertType="infoDialog.type"
        :title="infoDialog.title"
        :message="infoDialog.message"
        :show.sync="infoDialog.show"/>
      <FileViewer :file.sync="fileToPreview" />
  </v-dialog>
</template>

<script>
import DateField from "@/components/common/DateField.vue"
import AmountField from "@/components/common/AmountField.vue"
import FileViewer from "@/components/common/FileViewer.vue"
import { uuid, TransactionBuilder, Entry, ExternalFile } from "@/model";
import ConfirmationDialog from '@/components/common/ConfirmationDialog.vue';


function emptyEntry() {
  return {
    uuid: uuid(),
    account: undefined,
    amount: undefined,
  }
}

function initialState() {
  return {
    uuid: undefined,
    date : new Date(),
    transactionType: undefined,
    transactionAmount: undefined,
    counterparty: undefined,
    attachments: [],
    description: "",
    entries: [emptyEntry(), emptyEntry()],
  }
}

export default {
  components: {
    DateField,
    AmountField,
    ConfirmationDialog,
    FileViewer
  },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    transaction: {
      type: Object,
      default: undefined
    }
  },
  created() {
  },
  mounted() {
  },
  data: () => ({
    mode: "simple",
    txObj: initialState(),
    filesToUpload: [],
    fileToPreview: undefined,
    infoDialog: {type: "info", title: "", message: "", show: false }
  }),
  computed: {
    transactionTypes() {
      return this.$store.state.transactionTypes;
    },
    accounts() {
      return this.$store.state.accounts;
    },
    counterparties() {
      return this.$store.state.counterparties;
    },
    defaultCounterparty() {
      return this.$store.state.defaultCounterparty;
    }
  },
  methods: {
    isSimpleMode() {
      return this.mode === "simple";
    },
    isAdvancedMode() {
      return this.mode === "advanced";
    },
    advancedMode() {
      this.mode = "advanced";
    },
    simpleMode() {
      this.mode = "simple";
    },
    onAddEntry() {
      this.txObj.entries.push(emptyEntry());
    },
    onRemoveEntry(entry) {
      const index = this.txObj.entries.findIndex(e => e.uuid === entry.uuid);
      if (index >= 0) {
        this.txObj.entries.splice(index, 1);
      } 
    },
    updateEntries() {
      if (!this.txObj.transactionType || !this.txObj.transactionAmount || !this.txObj.transactionAmount.value) {
        return;
      }
      const entries = this.txObj.transactionType.getEntries(this.txObj.transactionAmount);
      this.txObj.entries = entries.map(ent => ({
        uuid: ent.uuid,
        account: ent.account.obj(),
        amount: ent.amount
      }));
    },
    showError(message) {
      this.infoDialog = {
        type: "error",
        title: "Error",
        message: message,
        show: true,
      }
    },
    async onSave() {
      const entries = this.txObj.entries.map(ent => new Entry(ent.uuid, ent.account.getRef(), ent.amount));
      const cpRef = this.txObj.counterparty ? this.txObj.counterparty.getRef() : this.defaultCounterparty.getRef();
      
      const attachments = [];
      const files = [];
      const blobs = [];
      for (let i = 0; i < this.txObj.attachments.length; i++) {
        const attachment = this.txObj.attachments[i];
        if (!attachment.obj) {
          const extFile = await ExternalFile.fromFile(attachment.file, attachment.uuid);
          files.push(extFile);
          blobs.push(extFile.blob.obj());
          attachments.push(extFile.getCachingRef());
        }
        else {
          attachments.push(attachment.obj.getRef());
        }
      }

      let txDraft = new TransactionBuilder()
        .uuid(this.txObj.uuid || uuid())
        .date(this.txObj.date)
        .description(this.txObj.description)
        .counterparty(cpRef)
        .addEntries(entries)
        .addAttachements(attachments);

      if (this.txObj.transactionType) {
        txDraft = txDraft.transactionType(this.txObj.transactionType.getRef());
      }

      const transaction = txDraft.build();
      
      try {
        transaction.validate();
        await this.$store.dispatch("saveTransaction", {transaction: transaction, files: files, blobs: blobs});
        this.onClose(transaction);
      } catch (e) {
        console.error(e);
        this.showError(e.message)
      }
    },
    onClose(transaction) {
      this.txObj = initialState();
      this.filesToUpload = [];
      this.$emit("update:show", false);
      this.$emit("update:transaction", transaction);
    },
    async onPreview(attachment) {
      if (!(attachment.file) && attachment.obj ) {
        this.fileToPreview = await attachment.obj.getFile();
        return;
      }
      this.fileToPreview = attachment.file;
    },
    onRemoveAttachment(attachment) {
      const index = this.txObj.attachments.indexOf(attachment);
      if (index >= 0) {
        this.txObj.attachments.splice(index, 1);
      }
    }
  },
  watch : {
    async transaction() {
      if (!this.transaction) {
        return;
      }
      this.txObj = {
        uuid: this.transaction.uuid,
        date : this.transaction.date,
        counterparty: getRefObj(this.transaction.counterparty),
        description: this.transaction.description,
        transactionType: getRefObj(this.transaction.transactionType),
        transactionAmount: extractFlowAmount(this.transaction.entries),
        entries: this.transaction.entries.map(ent => ({
          uuid: ent.uuid,
          account: ent.account.obj(),
          amount: ent.amount
        })),
        attachments: this.transaction.attachments.map(attachmentRef => {
          const attachment = attachmentRef.obj();
          return {
            uuid: attachment.uuid,
            name: attachment.name,
            type: attachment.type,
            file: undefined,
            obj: attachment
          }
        })        
      }
      if (!this.transaction.transactionType) {
        this.advancedMode();
      }
    },
    filesToUpload() {
      if (this.filesToUpload.length > 0 ) {
        this.filesToUpload.map(file => {
          return {
            uuid: uuid(),
            name: file.name,
            type: file.type,
            file: file,
            obj: undefined
          };
        })
        .forEach(attachment => {
          this.txObj.attachments.push(attachment);
        });
        this.filesToUpload = [];
      }
    }
  }
};
function extractFlowAmount(entries) {
  const amounts = entries
    .map(ent => ent.amount)
    .filter(amount => amount.value > 0);
  
  if (amounts.length > 0 ) {
    return amounts[0];
  }

  return amounts.reduce((a,b) => a.add(b));
}

function getRefObj(entity) {
  if (entity) {
    return entity.obj();
  }
  return undefined;
}
</script>